<template>
    <div class="firebase-settings">
        <app-overlay-loader v-if="preloader"/>
        <div v-else>
            <h2 class="mb-4">{{ $t('firebase_settings') }}</h2>
            <form 
              ref="form" 
              :data-url="apiUrl"
              enctype="multipart/form-data"
              class="mb-0"
              :class="{'loading-opacity': preloader}">
            
            <!-- Two Column Layout -->
            <div class="row">
                <!-- Left Column -->
                <div class="col-md-6">
                    <!-- Firebase API Key -->
                    <div class="form-group row align-items-center">
                        <label for="api_key" class="col-lg-4 col-xl-4 mb-lg-0">
                            {{ $t('firebase_api_key') }}
                        </label>
                        <div class="col-lg-8 col-xl-8">
                            <app-input id="api_key"
                                       type="text"
                                       v-model="formData.api_key"
                                       :placeholder="$t('enter_firebase_api_key')"/>
                        </div>
                    </div>
                    
                    <!-- Firebase Project ID -->
                    <div class="form-group row align-items-center">
                        <label for="project_id" class="col-lg-4 col-xl-4 mb-lg-0">
                            {{ $t('firebase_project_id') }}
                        </label>
                        <div class="col-lg-8 col-xl-8">
                            <app-input id="project_id"
                                       type="text"
                                       v-model="formData.project_id"
                                       :placeholder="$t('enter_firebase_project_id')"/>
                        </div>
                    </div>
                    
                    <!-- Firebase App ID -->
                    <div class="form-group row align-items-center">
                        <label for="app_id" class="col-lg-4 col-xl-4 mb-lg-0">
                            {{ $t('firebase_app_id') }}
                        </label>
                        <div class="col-lg-8 col-xl-8">
                            <app-input id="app_id"
                                       type="text"
                                       v-model="formData.app_id"
                                       :placeholder="$t('enter_firebase_app_id')"/>
                        </div>
                    </div>
                    
                    <!-- Firebase Vapid Key -->
                    <div class="form-group row align-items-center">
                        <label for="vapid_key" class="col-lg-4 col-xl-4 mb-lg-0">
                            {{ $t('firebase_vapid_key') }}
                        </label>
                        <div class="col-lg-8 col-xl-8">
                            <app-input id="vapid_key"
                                       type="text"
                                       v-model="formData.vapid_key"
                                       :placeholder="$t('enter_firebase_vapid_key')"/>
                        </div>
                    </div>
                    
                    <!-- Google Service File For Android -->
                    <div class="form-group row align-items-center">
                        <label for="google_service_android" class="col-lg-4 col-xl-4 mb-lg-0">
                            {{ $t('google_service_android') }} ({{ $t('json_file') }})
                        </label>
                        <div class="col-lg-8 col-xl-8">
                            <app-input id="google_service_android"
                                       type="file"
                                       v-model="selectedFiles.google_service_android"
                                       accept=".json"
                                       :label="getFileName('google_service_android')"/>
                        </div>
                    </div>
                </div>
                
                <!-- Right Column -->
                <div class="col-md-6">
                    <!-- Firebase Auth Domain -->
                    <div class="form-group row align-items-center">
                        <label for="auth_domain" class="col-lg-4 col-xl-4 mb-lg-0">
                            {{ $t('firebase_auth_domain') }}
                        </label>
                        <div class="col-lg-8 col-xl-8">
                            <app-input id="auth_domain"
                                       type="text"
                                       v-model="formData.auth_domain"
                                       :placeholder="$t('enter_firebase_auth_domain')"/>
                        </div>
                    </div>
                    
                    <!-- Firebase Messaging Sender ID -->
                    <div class="form-group row align-items-center">
                        <label for="messaging_sender_id" class="col-lg-4 col-xl-4 mb-lg-0">
                            {{ $t('firebase_messaging_sender_id') }}
                        </label>
                        <div class="col-lg-8 col-xl-8">
                            <app-input id="messaging_sender_id"
                                       type="text"
                                       v-model="formData.messaging_sender_id"
                                       :placeholder="$t('enter_firebase_messaging_sender_id')"/>
                        </div>
                    </div>
                    
                    <!-- Firebase Measurement ID -->
                    <div class="form-group row align-items-center">
                        <label for="measurement_id" class="col-lg-4 col-xl-4 mb-lg-0">
                            {{ $t('firebase_measurement_id') }}
                        </label>
                        <div class="col-lg-8 col-xl-8">
                            <app-input id="measurement_id"
                                       type="text"
                                       v-model="formData.measurement_id"
                                       :placeholder="$t('enter_firebase_measurement_id')"/>
                        </div>
                    </div>
                    
                    <!-- Service Account Key File -->
                    <div class="form-group row align-items-center">
                        <label for="service_account_key" class="col-lg-4 col-xl-4 mb-lg-0">
                            {{ $t('service_account_key') }} ({{ $t('json_file') }})
                        </label>
                        <div class="col-lg-8 col-xl-8">
                            <app-input id="service_account_key"
                                       type="file"
                                       v-model="selectedFiles.service_account_key"
                                       accept=".json"
                                       :label="getFileName('service_account_key')"/>
                        </div>
                    </div>
                    
                    <!-- Google Service File For IOS -->
                    <div class="form-group row align-items-center">
                        <label for="google_service_ios" class="col-lg-4 col-xl-4 mb-lg-0">
                            {{ $t('google_service_ios') }} ({{ $t('plist_file') }})
                        </label>
                        <div class="col-lg-8 col-xl-8">
                            <app-input id="google_service_ios"
                                       type="file"
                                       v-model="selectedFiles.google_service_ios"
                                       accept=".plist,.xml"
                                       :label="getFileName('google_service_ios')"/>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Action Buttons -->
            <div class="mt-5 action-buttons">
                <button class="btn btn-primary mr-2" @click.prevent="submit">
                    {{ $t('save') }}
                </button>
                <button class="btn btn-secondary" @click.prevent="cancel">
                    {{ $t('cancel') }}
                </button>
            </div>
            </form>
        </div>
    </div>
</template>

<script>
import {FormMixin} from "../../../../core/mixins/form/FormMixin";
import * as actions from "../../../Config/ApiUrl";

export default {
    name: "FirebaseSetting",
    mixins: [FormMixin],
    data() {
        return {
            preloader: false,
            apiUrl: actions.SAVE_FIREBASE_SETTINGS,
            formData: {
                api_key: '',
                project_id: '',
                app_id: '',
                vapid_key: '',
                auth_domain: '',
                messaging_sender_id: '',
                measurement_id: '',
            },
            selectedFiles: {
                google_service_android: null,
                service_account_key: null,
                google_service_ios: null,
            },
            existingFiles: {
                android: null,
                ios: null,
                serviceAccount: null,
            },
        }
    },
    created() {
        this.loadSettings();
    },
    methods: {
        loadSettings() {
            this.preloader = true;
            let url = actions.GET_FIREBASE_SETTINGS;
            
            this.axiosGet(url).then(response => {
                if (response && response.data) {
                    // Handle different response structures
                    let data = null;
                    if (response.data.success && response.data.data) {
                        data = response.data.data;
                    } else if (response.data.data && response.data.data.data) {
                        // Nested data structure
                        data = response.data.data.data;
                    } else if (!response.data.success && response.data.data) {
                        // Direct data
                        data = response.data.data;
                    }
                    
                    if (data) {
                        this.formData = {
                            api_key: data.api_key || '',
                            project_id: data.project_id || '',
                            app_id: data.app_id || '',
                            vapid_key: data.vapid_key || '',
                            auth_domain: data.auth_domain || '',
                            messaging_sender_id: data.messaging_sender_id || '',
                            measurement_id: data.measurement_id || '',
                        };
                        
                        this.existingFiles = {
                            android: data.google_service_android_url || null,
                            ios: data.google_service_ios_url || null,
                            serviceAccount: data.service_account_key_path || null,
                        };
                    }
                }
            }).catch((error) => {
                // Silently fail - show form anyway
                console.log('Firebase settings not loaded:', error);
                // Don't show error toast on initial load if API doesn't exist yet
            }).finally(() => {
                this.preloader = false;
            });
        },
        
        getFileName(fieldName) {
            if (this.selectedFiles[fieldName]) {
                return this.selectedFiles[fieldName].name || this.$t('file_uploaded');
            }
            const existingFileMap = {
                google_service_android: 'android',
                google_service_ios: 'ios',
                service_account_key: 'serviceAccount'
            };
            if (this.existingFiles[existingFileMap[fieldName]]) {
                return this.$t('file_uploaded');
            }
            return this.$t('no_file_chosen');
        },
        
        beforeSubmit() {
            this.preloader = true;
        },
        
        submit() {
            let formData = new FormData();
            
            // Add all text fields (even empty ones - they're optional)
            Object.keys(this.formData).forEach(key => {
                const value = this.formData[key];
                // Send empty string instead of skipping
                formData.append(key, value !== null && value !== undefined ? value : '');
            });
            
            // Add files if selected
            Object.keys(this.selectedFiles).forEach(key => {
                if (this.selectedFiles[key]) {
                    formData.append(key, this.selectedFiles[key]);
                }
            });
            
            // Debug: Log what we're sending
            console.log('Submitting Firebase Settings:', {
                formData: Object.fromEntries(formData.entries()),
                hasFiles: Object.keys(this.selectedFiles).some(k => this.selectedFiles[k])
            });
            
            this.save(formData);
        },
        
        afterSuccess(res) {
            // Check if response indicates actual success
            if (res && res.data) {
                if (res.data.success === false) {
                    // API returned success: false
                    this.$toastr.e(res.data.message || this.$t('failed_to_save_settings'));
                    return;
                }
                
                // Only show success if we actually have data or a success message
                if (res.data.message || res.data.data) {
                    this.$toastr.s(res.data.message || this.$t('settings_saved_successfully'));
                    // Clear selected files after successful save
                    this.selectedFiles = {
                        google_service_android: null,
                        service_account_key: null,
                        google_service_ios: null,
                    };
                    // Reload settings to get updated file URLs
                    this.loadSettings();
                } else {
                    // No data and no message - something went wrong
                    this.$toastr.e(this.$t('failed_to_save_settings'));
                }
            } else {
                this.$toastr.e(this.$t('failed_to_save_settings'));
            }
        },
        
        afterError(res) {
            if (res && res.data) {
                if (res.data.errors) {
                    // Handle validation errors
                    Object.keys(res.data.errors).forEach(key => {
                        this.$toastr.e(res.data.errors[key][0]);
                    });
                } else {
                    this.$toastr.e(res.data.message || this.$t('failed_to_save_settings'));
                }
            } else {
                this.$toastr.e(this.$t('failed_to_save_settings'));
            }
        },
        
        afterFinalResponse() {
            this.preloader = false;
        },
        
        cancel() {
            // Reset form by reloading settings
            this.loadSettings();
        }
    }
}
</script>

<style scoped>
.firebase-settings {
    padding: 2rem;
    background: white;
}

@media (max-width: 768px) {
    .firebase-settings .row > div {
        margin-bottom: 1rem;
    }
}
</style>

